/* eslint-disable @eslint-react/no-clone-element */
import classNames from 'classnames';
import type {
  FunctionComponent,
  PropsWithChildren,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';
import { cloneElement } from 'react';

import { forTemplate } from '@trello/legacy-i18n';
import { CloseIcon } from '@trello/nachos/icons/close';
import { DownIcon } from '@trello/nachos/icons/down';
import { StarIcon } from '@trello/nachos/icons/star';
import { RouterLink } from '@trello/router/router-link';
import type { NavigationTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import {
  useWorkspaceNavigation,
  useWorkspaceNavigationHidden,
} from '@trello/workspace-navigation';

import type { CategoryData, Classes } from 'app/src/components/Templates/types';

import styles from './Navigation.less';

const format = forTemplate('navigation');

interface NavigationProps {
  categories: CategoryData[];
  classes?: Classes;
  navigationExpanded: boolean;
  setNavigationExpanded: (val: boolean) => void;
  urlSlug: string;
  analyticsFn: (key: string) => void;
  viewAllIcon: JSX.Element;
  boardIcon: JSX.Element;
  SearchComponent?: ReactNode;
  section?: string | null;
  subsection?: string | null;
}

const defaultClasses = { navigationLink: '' };
export const Navigation: FunctionComponent<
  PropsWithChildren<NavigationProps>
> = ({
  categories,
  classes = defaultClasses,
  navigationExpanded,
  setNavigationExpanded,
  urlSlug,
  analyticsFn,
  viewAllIcon,
  boardIcon,
  SearchComponent,
  section,
  subsection,
  children,
}) => {
  const [{ expanded: _workspaceNavigationExpanded }] = useWorkspaceNavigation();
  const [{ hidden: _workspaceNavigationHidden }] =
    useWorkspaceNavigationHidden();
  const workspaceNavigationExpanded =
    !_workspaceNavigationHidden && _workspaceNavigationExpanded;

  const categoryElements = categories.map((categoryData) => {
    return (
      <li key={categoryData.key}>
        <RouterLink
          onClick={(e: ReactMouseEvent<HTMLAnchorElement>) => {
            setNavigationExpanded(false);
            analyticsFn(categoryData.key);
          }}
          href={`/${urlSlug}/${categoryData.url}`}
          className={classNames({
            [styles.navigationLink]: true,
            [styles.categoryLink]: true,
            [styles.isActive]:
              section === 'category' && subsection === categoryData.key,
            [classes.navigationLink]: true,
            [categoryData.classes?.navigationLink || '']: true,
          })}
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
          dangerouslySetInnerHTML={{ __html: categoryData.title }}
        />
      </li>
    );
  });

  return (
    <div
      className={classNames(styles.directorySidebar, {
        [styles.workspaceNavigationExpanded]: workspaceNavigationExpanded,
      })}
    >
      {!navigationExpanded && (
        <div
          className={styles.responsiveNavigator}
          onClick={() => setNavigationExpanded(true)}
          role="button"
        >
          {format('view-all-categories')}
          <DownIcon dangerous_className={styles.responsiveNavigatorIcon} />
        </div>
      )}

      <div
        className={classNames(styles.navigation, {
          [styles.expanded]: navigationExpanded,
        })}
      >
        <div
          className={styles.responsiveNavigator}
          onClick={() => setNavigationExpanded(false)}
          role="button"
        >
          {format('hide-categories')}
          <CloseIcon dangerous_className={styles.responsiveNavigatorIcon} />
        </div>

        {SearchComponent && (
          <div
            className={styles.navigationSection}
            data-testid={getTestId<NavigationTestIds>('nav-search-input')}
          >
            {SearchComponent}
          </div>
        )}

        <div className={styles.navigationSection}>
          <ul>
            <li>
              <RouterLink
                onClick={() => setNavigationExpanded(false)}
                href={`/${urlSlug}`}
                className={classNames({
                  [styles.navigationLink]: true,
                  [styles.isActive]:
                    typeof section === 'undefined' || section === null,
                  [classes.navigationLink]: true,
                })}
              >
                <StarIcon
                  size="small"
                  dangerous_className={styles.navigationLinkIcon}
                />
                {format('featured')}
              </RouterLink>
            </li>
            {urlSlug !== 'templates' && (
              <>
                <li>
                  <RouterLink
                    onClick={() => setNavigationExpanded(false)}
                    href={`/${urlSlug}/all`}
                    className={classNames({
                      [styles.navigationLink]: true,
                      [styles.isActive]: section === 'all',
                      [classes.navigationLink]: true,
                    })}
                  >
                    {cloneElement(viewAllIcon, {
                      size: 'small',
                      dangerous_className: styles.navigationLinkIcon,
                    })}
                    {format('view-all')}
                  </RouterLink>
                </li>
                <li>
                  <RouterLink
                    onClick={() => setNavigationExpanded(false)}
                    href={`/${urlSlug}/made-by-trello`}
                    className={classNames({
                      [styles.navigationLink]: true,
                      [styles.isActive]: section === 'made-by-trello',
                      [classes.navigationLink]: true,
                    })}
                  >
                    {cloneElement(boardIcon, {
                      size: 'small',
                      dangerous_className: styles.navigationLinkIcon,
                    })}
                    {format('made-by-trello')}
                  </RouterLink>
                </li>
              </>
            )}
          </ul>
        </div>

        <div className={styles.navigationSection}>
          <ul>{categoryElements}</ul>
        </div>
        {children}
      </div>
    </div>
  );
};
